import { useState } from "react";
import { UserLayout } from "./components/layout/UserLayout";
import Container from "./components/Container";
import Text from "./components/Text";
import Effects from "./components/Effects";
import BlogCard from "./components/cards/BlogCard";
import PageHero from "./components/PageHero";
import { getImg } from "../../Components/Utils";
import Img from "./components/Img";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import { useFetchBlogPageQuery } from "../../stores/api/pages.api";

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const BlogSkeleton = () => (
  <UserLayout>
    <Container variant="xnarrow" className="px-4 lg:px-8 py-12">
      <div className="flex flex-col lg:flex-row gap-10">
        <aside className="w-full lg:w-[280px] flex-shrink-0 space-y-10">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-60 w-full" />
          <Skeleton className="h-32 w-full" />
        </aside>
        <main className="flex-1 min-w-0">
          <Skeleton className="aspect-[16/9] w-full mb-4" />
          <Skeleton className="h-6 w-3/4 mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <Skeleton className="aspect-[1.5/1] w-full mb-3" />
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        </main>
      </div>
    </Container>
  </UserLayout>
);

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const { data, isLoading } = useFetchBlogPageQuery();

  if (isLoading) return <BlogSkeleton />;

  const {
    categories = [],
    recentPosts = [],
    tags = [],
    instagram = [],
    posts = [],
  } = data ?? {};

  const [featured, ...rest] = posts;

  const filtered = activeCategory
    ? rest.filter(
        (p) => p.category.toLowerCase() === activeCategory.toLowerCase(),
      )
    : rest;

  return (
    <UserLayout>
      <PageHero
        title="All Blogs"
        crumbs={[
          { label: "Home", href: ROUTE_PATHS.HOME },
          { label: "Fashion" },
        ]}
      />

      <Container variant="narrow" className="px-4 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* ── Aside ── */}
          <aside className="w-full lg:w-[280px] flex-shrink-0 space-y-10 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
            {/* Blog categories */}
            <div>
              <Text
                is="h3"
                className="text-sm font-semibold text-gray-900 mb-4"
              >
                Blog categories
              </Text>
              <ul className="space-y-2">
                {categories.map((cat) => (
                  <li key={cat.label}>
                    <button
                      onClick={() =>
                        setActiveCategory(
                          activeCategory === cat.label ? null : cat.label,
                        )
                      }
                      className={`text-sm transition-colors hover:text-blue-500 ${activeCategory === cat.label ? "text-blue-500 font-medium" : "text-gray-500"}`}
                    >
                      {cat.label} ({cat.count})
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recent Posts */}
            <div>
              <Text
                is="h3"
                className="text-sm font-semibold text-gray-900 mb-4"
              >
                Recent Post
              </Text>
              <ul className="space-y-4">
                {recentPosts.map((post) => (
                  <li key={post.slug}>
                    <Text
                      is="a"
                      href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", post.slug)}
                      className="flex items-start gap-3 group"
                    >
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded">
                        <Img
                          src={getImg(post.img)}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <Text
                          is="span"
                          className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded uppercase tracking-wider block mb-1"
                        >
                          {post.category}
                        </Text>
                        <Text
                          is="p"
                          className="text-xs text-gray-700 group-hover:text-blue-500 transition-colors leading-snug"
                        >
                          {post.title}
                        </Text>
                      </div>
                    </Text>
                  </li>
                ))}
              </ul>
            </div>

            {/* Blog tags */}
            <div>
              <Text
                is="h3"
                className="text-sm font-semibold text-gray-900 mb-4"
              >
                Blog tags
              </Text>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Text
                    key={tag}
                    is="a"
                    href="#"
                    className="text-xs border border-gray-200 px-3 py-1.5 text-gray-600 hover:border-gray-900 hover:text-gray-900 transition-colors"
                  >
                    {tag}
                  </Text>
                ))}
              </div>
            </div>

            {/* Instagram */}
            <div>
              <Text
                is="h3"
                className="text-sm font-semibold text-gray-900 mb-4"
              >
                Instagram
              </Text>
              <div className="grid grid-cols-3 gap-1.5">
                {instagram.map((img, i) => (
                  <div
                    key={i}
                    className="aspect-square overflow-hidden rounded"
                  >
                    <Img
                      src={getImg(img)}
                      alt=""
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* ── Main blog grid ── */}
          <main className="flex-1 min-w-0">
            {/* Featured post */}
            {featured && (
              <Effects type="scroll-fadeup">
                <div className="mb-8">
                  <div className="relative overflow-hidden rounded-xl aspect-[16/9] mb-4">
                    <Img
                      src={getImg(featured.img)}
                      alt={featured.title}
                      className="w-full h-full object-cover"
                    />
                    <Text
                      is="span"
                      className="absolute top-4 left-4 text-[10px] font-semibold bg-white text-gray-700 px-2.5 py-1 uppercase tracking-wider rounded"
                    >
                      {featured.category}
                    </Text>
                  </div>
                  <Text
                    is="h2"
                    className="text-xl font-bold text-gray-900 mb-2 hover:text-blue-500 transition-colors cursor-pointer"
                  >
                    <Text
                      is="a"
                      href={ROUTE_PATHS.BLOG_DETAIL.replace(
                        ":slug",
                        featured.slug,
                      )}
                    >
                      {featured.title}
                    </Text>
                  </Text>
                  <Text
                    is="a"
                    href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", featured.slug)}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 underline hover:text-gray-900 transition-colors"
                  >
                    Read more ↗
                  </Text>
                </div>
              </Effects>
            )}

            {/* Post grid */}
            <Effects type="scroll-fadeup">
              <Container
                variant="full"
                showEmptyState={!data || !posts.length}
                emptyMessage="No blog posts found"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filtered.map((post, i) => (
                    <BlogCard
                      key={i}
                      img={post.img}
                      hoverImg={post.hoverImg}
                      title={post.title}
                      description={post.description}
                      href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", post.slug)}
                      category={post.category?.name ?? post.category ?? null}
                    />
                  ))}
                </div>
              </Container>
            </Effects>
          </main>
        </div>
      </Container>
    </UserLayout>
  );
};

export default Blog;
